import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import type { tEmbeddedSignupPath } from 'src/lib/facebook/embeddedSignupLogin';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_ICON_CLASS_NAME,
  WIZARD_PATH_CARDS,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';

export type tWhatsAppConnectionWizardConnectionSummaryProps = {
  /** The path launched in this session; absent shows no path. */
  selectedPath?: tEmbeddedSignupPath;
  /** The connected number, readable; absent shows no number. */
  phoneNumber?: string;
};

/**
 * The "Tu conexión" lines of the aside once Meta answered: the chosen path and
 * what it means when this session launched it, and the number when the
 * backend sent it, so a screen that talks about "this number" shows which one.
 *
 * @param {tWhatsAppConnectionWizardConnectionSummaryProps} props - The path
 * and the number.
 * @returns {JSX.Element | null} The summary, or null when neither is known.
 */
export default function WhatsAppConnectionWizardConnectionSummary(
  props: tWhatsAppConnectionWizardConnectionSummaryProps,
) {
  const { selectedPath, phoneNumber } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const pathCard = WIZARD_PATH_CARDS.find((card) => card.path === selectedPath);
  if (!pathCard && !phoneNumber) return null;

  return (
    <div className="flex gap-[var(--ls-spacing-2)]">
      <DevicePhoneMobileIcon
        className={WIZARD_ICON_CLASS_NAME}
        aria-hidden="true"
      />
      <div className="flex flex-col gap-[var(--ls-spacing-1)]">
        {pathCard && (
          <p className={WIZARD_BODY_CLASS_NAME}>
            <Trans
              t={t}
              i18nKey="wizard.asideConnection.chosenPath"
              values={{ path: t(pathCard.nameKey) }}
              components={{ strong: <strong className="font-medium" /> }}
            />
          </p>
        )}
        {phoneNumber && (
          // The account's own number stays out of session replays.
          <p
            className={classNames(
              WIZARD_BODY_CLASS_NAME,
              'ph-no-capture font-medium',
            )}
          >
            {phoneNumber}
          </p>
        )}
        {pathCard && (
          <p className="text-[length:var(--ls-body)] text-[var(--ls-text-neutral-light)]">
            {t(pathCard.chosenBlurbKey)}
          </p>
        )}
      </div>
    </div>
  );
}
